import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { RowActionsProps } from './rowActions.types.js';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { focusRing } from '../lib/focus.js';
import { useAnchoredPanel } from '../lib/useAnchoredPanel.js';
import { useDismiss } from '../lib/useDismiss.js';
import { portalHost } from '../lib/portalHost.js';

/* ===========================================================================
 * RowActions — the ⋯ menu at the end of a table row.
 *
 * WHY IT EXISTS, and it is not tidiness.
 *
 * A list is for FINDING and COMPARING. Eleven tables in this product ended
 * their rows with a live "Delete" — a control that destroys a record, drawn at
 * the same weight as the data beside it, repeated once per row, reachable by a
 * single mis-aimed click on a 44px target on a tablet held one-handed on a
 * building site. Nothing about that is a decision anyone took: it is what
 * happens when eleven screens each answer "where does delete go" separately.
 *
 * The rule this component makes mechanical:
 *
 *     A ROW NEVER SHOWS A DESTRUCTIVE CONTROL. The one action a screen exists
 *     for may stay visible (Approve on the device queue); everything that
 *     removes, revokes or refuses lives behind a deliberate open.
 *
 * A convention could not enforce that, because a convention has no failure
 * mode. Two things here are checkable instead: `destructive` is a property of
 * an ITEM rather than a class somebody remembers to add, and there is no way to
 * spell a menu item without a label — so a red glyph with no word, which is
 * what four of those eleven tables shipped, is unspellable.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT IS NOT.
 *
 * It is NOT a second home for the primary action. A screen whose purpose is to
 * decide (approvals) keeps its ordinary verb on the row; putting Approve behind
 * a menu adds a click to the only thing that queue is for. The menu holds the
 * exceptional and the irreversible — which on that same queue is Refuse and
 * Revoke, and on a settings list is Delete.
 *
 * It is NOT a dialog. `role="menu"` with roving arrow keys, not a focus trap:
 * a menu is a list of verbs you pick one of and leave, and trapping focus in
 * six words is how a keyboard user gets stuck in a control that has no OK.
 *
 * ---------------------------------------------------------------------------
 * GEOMETRY AND PLACEMENT are lifted from `FiltersPopover` deliberately, not
 * re-derived: same portal, same fixed placement measured off the trigger's own
 * rect, same capture-phase scroll and resize listeners, same inline-start
 * alignment so the panel drops from the other side under Arabic.
 *
 * The clipping problem is worse here than it is there, which is why none of it
 * is optional. This trigger lives in the LAST CELL of a table that scrolls
 * inside an `overflow-hidden rounded-card` Card. An absolutely-positioned menu
 * would be clipped by a box that knows nothing about it — and the row it hangs
 * off is often the last one, so the clip would land on the item most likely to
 * be Delete.
 * ======================================================================== */

export type { RowActionItem, RowActionsProps } from './rowActions.types.js';

/** The menu's glyph size, and the item glyph size. One constant each. */
const TRIGGER_ICON = 16;

/**
 * The ⋯ trigger plus its menu.
 *
 * Renders nothing at all when every item is filtered out by the caller (a role
 * gate that removed the only verb), because an empty menu is a control that
 * promises something and then says "no". Callers pass an already-filtered list.
 */
export function RowActions({
  items,
  label,
  className,
  trigger,
  triggerClassName,
  open: openProp,
  onOpenChange,
  footnote,
}: RowActionsProps): JSX.Element | null {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  /* One setter for both modes, so every `setOpen(...)` below is unchanged and
     cannot forget to notify a controlled parent. The internal state is written
     in controlled mode too and simply ignored, so a caller that stops passing
     `open` mid-life does not resurrect a stale value. */
  const setOpen = useCallback(
    (next: boolean): void => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  /* ORDINARY FIRST, DESTRUCTIVE LAST, enforced here rather than asked for in a
     comment. A stable sort, so two ordinary items keep the order the caller
     wrote them in — the component decides the BANDS, the caller decides what is
     in each. */
  const ordered = [
    ...items.filter((i) => !i.destructive),
    ...items.filter((i) => i.destructive),
  ];
  const firstDestructive = ordered.findIndex((i) => i.destructive);

  const close = useCallback(
    (restoreFocus: boolean) => {
      setOpen(false);
      if (restoreFocus) triggerRef.current?.focus();
    },
    [setOpen]
  );

  /* Placed in the VIEWPORT, not in the row — see the header note. The
     measurement, the flip and the RTL mirroring live in `useAnchoredPanel`
     because `FiltersPopover` needs the identical arithmetic and two copies of
     "which way does this open" drift the first time one is adjusted. */
  const pos = useAnchoredPanel(open, triggerRef, panelRef);

  /* THE FIRST ITEM TAKES FOCUS ON OPEN. A menu whose focus stays on the trigger
     leaves a keyboard user pressing Down to reach something the pointer already
     sees, and leaves a screen reader announcing the trigger twice. */
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => itemRefs.current[0]?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  /* Escape closes THIS menu and not also whatever is behind it — a row menu
     opened inside a dialog must not take the dialog down with it. The panel is
     portalled, so it is no longer inside the anchor: both are "inside". No
     focus restore on an outside press: the reader is already looking somewhere
     else. Capture phase, so the menu closes even when the press lands on a row
     that stops propagation. All of that is `useDismiss`. */
  useDismiss(open, [anchorRef, panelRef], (how) => close(how === 'escape'));

  if (ordered.length === 0) return null;

  /** Roving focus. Home/End included: a six-item menu still has a last item. */
  function onItemKeyDown(e: React.KeyboardEvent, index: number): void {
    const last = ordered.length - 1;
    let next: number | null = null;
    if (e.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    else if (e.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    else if (e.key === 'Tab') {
      // Tab leaves the menu rather than cycling inside it. This is the
      // behaviour that makes a menu not a dialog: there is nothing here to
      // fill in, so moving on should move on.
      close(false);
      return;
    }
    if (next === null) return;
    e.preventDefault();
    itemRefs.current[next]?.focus();
  }

  return (
    // `relative` anchors nothing visually — the panel is fixed — but it is what
    // the outside-click test measures against, so a click on the TRIGGER is not
    // "outside" and cannot reopen what it just closed.
    <div ref={anchorRef} className={cn('relative inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        /* The tooltip is for the GLYPH — a ⋯ with no hover text has no name
           for a pointer user. A trigger already showing a word or a figure gets
           none: a tooltip covering the thing it names is noise. */
        title={trigger ? undefined : label}
        onClick={() => (open ? close(true) : setOpen(true))}
        onKeyDown={(e) => {
          // Down/Up open the menu from the keyboard, which is the platform
          // behaviour every menu button has and the reason a keyboard user does
          // not have to guess that Enter is the only way in.
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={cn(
          // `.state-layer` rather than a hand-written hover: the same
          // currentColor hover/focus/press ramp `Button` and `IconButton` use,
          // so this darkens and presses like the rest of the family in either
          // theme and for any tenant hue.
          'state-layer inline-grid place-items-center',
          // Square, and sized by the CONTROL floor on BOTH axes: an icon-only
          // control that clears 38px vertically and 16px horizontally is not a
          // 38px target, it is a sliver.
          'min-h-control min-w-control rounded-control text-foreground-tertiary',
          '[@media(pointer:coarse)]:min-h-touch [@media(pointer:coarse)]:min-w-touch',
          'transition-colors duration-fast ease-standard',
          // Quiet at rest — it sits in every row of the table, so anything
          // louder than tertiary ink turns a column of them into a pattern the
          // eye has to ignore. It resolves to full ink on hover and focus.
          'hover:text-foreground',
          focusRing,
          // LAST, so it wins the merge. See `triggerClassName`.
          triggerClassName
        )}
      >
        {trigger ?? <MoreHorizontal size={TRIGGER_ICON} aria-hidden="true" />}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={menuId}
            // Opened from inside a Dialog (a task's ⋯ menu), this panel is
            // portalled OUTSIDE the dialog's focus trap — which refuses every
            // outside click except on a [data-portal-surface]. Unmarked, the
            // menu opened and none of its items could be clicked.
            data-portal-surface=""
            role="menu"
            aria-label={label}
            /* Parked off-screen until the layout effect has measured, rather
               than flashed at 0,0. `useLayoutEffect` runs before paint, so this
               is only ever drawn if the trigger has gone away. */
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              ...(pos ? { maxHeight: pos.maxHeight, overflowY: 'auto' as const } : {}),
            }}
            className={cn(
              // z-50, not z-40: a Dialog draws at z-50, and a menu opened in
              // one must paint over it (the DatePopover lesson, same tie).
              'fixed z-50 min-w-menu max-w-popover',
              // `border-subtle`, not `border`: this is a CONTAINER, and the
              // handoff draws a container's edge one step lighter than a
              // control's. Same pairing as the filters panel it hangs beside.
              'rounded-toolbar border border-border-subtle bg-surface p-1 shadow-popover',
              'focus-visible:outline-none'
            )}
          >
            {ordered.map((item, index) => {
              const blocked = Boolean(item.blockedBy);
              return (
                <div key={item.label}>
                  {/* The hairline that separates "change this" from "destroy
                      this". Drawn between the bands, never inside one, and only
                      when both bands exist. */}
                  {index === firstDestructive && index > 0 && (
                    <div className="my-1 h-px bg-border-subtle" aria-hidden="true" />
                  )}
                  {/* The band heading, drawn once — over the FIRST item naming
                      it, with the same hairline the destructive band gets, and
                      none when it opens the menu. */}
                  {item.group && item.group !== ordered[index - 1]?.group && (
                    <p
                      className={cn(
                        'px-2.5 pb-1 pt-2 text-label text-muted-foreground',
                        index > 0 && 'mt-1 border-t border-border-subtle'
                      )}
                    >
                      {item.group}
                    </p>
                  )}
                  <button
                    ref={(el) => {
                      itemRefs.current[index] = el;
                    }}
                    type="button"
                    role="menuitem"
                    disabled={blocked}
                    aria-describedby={blocked ? `${menuId}-b${index}` : undefined}
                    onKeyDown={(e) => onItemKeyDown(e, index)}
                    onClick={() => {
                      /* CLOSED BEFORE THE ACTION RUNS, and that ordering is
                         load-bearing. Most of these open a confirm dialog, and
                         a menu still sitting on top of the dialog it opened is
                         both a focus fight and a reader looking at a question
                         through a list of verbs. Focus is NOT restored to the
                         trigger: the dialog is about to take it. */
                      setOpen(false);
                      item.onSelect();
                    }}
                    className={cn(
                      'state-layer flex w-full flex-col items-start gap-0.5 rounded-control px-2.5 py-2 text-start',
                      'min-h-control [@media(pointer:coarse)]:min-h-touch',
                      'text-copy transition-colors duration-fast ease-standard',
                      item.destructive ? 'text-danger' : 'text-foreground',
                      'disabled:pointer-events-none disabled:opacity-50',
                      focusRing
                    )}
                  >
                    <span className="inline-flex w-full items-center justify-between gap-component">
                      <span className="inline-flex items-center gap-element">
                        {item.icon}
                        {item.label}
                      </span>
                      {/* The extension, on the trailing edge. See `trailing`. */}
                      {item.trailing && (
                        <span className="text-copy-sm text-muted-foreground" dir="ltr">
                          {item.trailing}
                        </span>
                      )}
                    </span>
                    {/* The second line, when the item can run. `blockedBy`
                        takes the same slot below when it cannot. */}
                    {!blocked && item.description && (
                      <span className="text-copy-sm text-muted-foreground">
                        {item.description}
                      </span>
                    )}
                    {/* The obstacle, under the label rather than in a tooltip
                        nobody opens. It is the whole reason `blockedBy` takes a
                        sentence instead of a boolean. */}
                    {blocked && (
                      <span
                        id={`${menuId}-b${index}`}
                        className="text-copy-sm text-muted-foreground"
                      >
                        {item.blockedBy}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
            {/* The menu's own closing caveat — not an item, so it is neither
                focusable nor pressable. See `footnote`. */}
            {footnote && (
              <p className="mt-1 border-t border-border-subtle px-2.5 pb-1 pt-2 text-copy-sm text-muted-foreground">
                {footnote}
              </p>
            )}
          </div>,
          portalHost()
        )}
    </div>
  );
}
